import { ConnectError } from "@connectrpc/connect";

export type PydanticError = {
  type: string;
  loc: (string | number)[];
  msg: string;
  input: unknown;
  ctx?: Record<string, unknown>;
};

export type MappedErrors = Record<string, string>;

const ERROR_TYPE_MAP: Record<string, string> = {
  // String errors
  string_type: "invalid-type",
  string_too_short: "too-short",
  string_too_long: "too-long",
  string_pattern_mismatch: "invalid-format",

  // Required/missing
  missing: "required",
  value_error: "value-error",

  // Email
  "value_error.email": "invalid-email",

  // Number errors
  int_type: "invalid-number",
  float_type: "invalid-number",
  greater_than: "too-small",
  greater_than_equal: "too-small",
  less_than: "too-large",
  less_than_equal: "too-large",

  // Date errors
  date_type: "invalid-date",
  date_from_datetime_inexact: "invalid-date",
  datetime_type: "invalid-date",

  // Boolean
  bool_type: "invalid-type",

  // Password specific (custom validators)
  password_too_short: "password-min",
  password_mismatch: "passwords-not-match",
};

const MESSAGE_PATTERNS: Array<
  [RegExp, string, ((match: RegExpMatchArray, error: PydanticError) => Record<string, string>)?]
> = [
  [/domain name .* does not exist/i, "email-domain-not-exist"],
  [/invalid email/i, "invalid-email"],
  [/email.*not.*valid/i, "invalid-email"],
  [/not a valid email/i, "invalid-email"],

  [/password.*(?:at least|minimum|min).* (\d+)/i, "password-min", (match) => ({ min: match[1] || "" })],
  [/password.*too short/i, "password-min"],
  [/passwords? (?:do not|don't) match/i, "passwords-not-match"],
  [
    /should have at least (\d+) character/i,
    "min-length",
    (_match, error) => ({ min: String(error.ctx?.min_length ?? _match[1]) }),
  ],
  [
    /should have at most (\d+) character/i,
    "max-length",
    (_match, error) => ({ max: String(error.ctx?.max_length ?? _match[1]) }),
  ],
  [
    /(?:at least|minimum|min).* (\d+) character/i,
    "min-length",
    (match, error) => ({ min: String(error.ctx?.min_length ?? match[1]) }),
  ],
  [
    /(?:at most|maximum|max).* (\d+) character/i,
    "max-length",
    (match, error) => ({ max: String(error.ctx?.max_length ?? match[1]) }),
  ],
  [/too short/i, "too-short"],
  [/too long/i, "too-long"],

  [/field required/i, "required"],
  [/missing/i, "required"],
  [/none is not an allowed/i, "required"],

  [/invalid.*format/i, "invalid-format"],
  [/not a valid/i, "invalid-format"],

  [/not a valid (?:integer|number)/i, "invalid-number"],
  [/greater than (\d+)/i, "min-value", (match) => ({ min: match[1] || "" })],
  [/less than (\d+)/i, "max-value", (match) => ({ max: match[1] || "" })],

  [/invalid.*date/i, "invalid-date"],
  [/not a valid date/i, "invalid-date"],

  [/only.*numbers/i, "name-not-numbers"],
  [/must contain.*letters/i, "name-must-contain-letters"],

  // Password complexity requirements

  [/value error/i, "value-error"],
];

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function extractFieldName(loc: (string | number)[]): string {
  const filtered = loc.filter((l): l is string => l !== "body" && typeof l === "string");
  const fieldName = (filtered.length > 0 ? filtered[filtered.length - 1] : "general") ?? "general";
  return snakeToCamel(fieldName);
}

function findTranslationByMessage(
  msg: string,
  error: PydanticError,
): { key: string; params?: Record<string, string> } | null {
  for (const [pattern, key, paramExtractor] of MESSAGE_PATTERNS) {
    const match = msg.match(pattern);
    if (match) {
      const params = paramExtractor ? paramExtractor(match, error) : undefined;
      return { key, params };
    }
  }
  return null;
}

export function mapSingleError(error: PydanticError): { key: string; params?: Record<string, string> } {
  const byMessage = findTranslationByMessage(error.msg, error);
  if (byMessage) {
    return byMessage;
  }

  const byType = ERROR_TYPE_MAP[error.type];
  if (byType) {
    return { key: byType };
  }

  return { key: "unknown-error" };
}

export function mapPydanticErrors(errors: PydanticError[]): MappedErrors {
  const result: MappedErrors = {};

  for (const error of errors) {
    const field = extractFieldName(error.loc);
    const { key } = mapSingleError(error);

    result[field] = `validation:${key}`;
  }

  return result;
}

function interpolate(template: string, params?: Record<string, string>): string {
  if (!params) return template;
  return template.replace(/{{(\w+)}}/g, (_, key) => params[key] ?? `{{${key}}}`);
}

export function mapPydanticErrorsWithTranslation(errors: PydanticError[], t: (key: string) => string): MappedErrors {
  const result: MappedErrors = {};

  for (const error of errors) {
    const field = extractFieldName(error.loc);
    const { key, params } = mapSingleError(error);

    const translated = t(`validation:${key}`);
    result[field] = interpolate(translated, params);
  }

  return result;
}

function isErrorArray(arr: unknown[]): arr is PydanticError[] {
  if (arr.length === 0) return false;

  const first = arr[0];
  return (
    typeof first === "object" &&
    first !== null &&
    "type" in first &&
    "loc" in first &&
    "msg" in first &&
    Array.isArray(first.loc)
  );
}

export function isPydanticErrorArray(data: unknown): data is PydanticError[] {
  if (Array.isArray(data)) {
    return isErrorArray(data);
  }
  return false;
}

export function isPydanticDetailResponse(data: unknown): data is { detail: PydanticError[] } {
  if (typeof data !== "object" || data === null) return false;
  if (!("detail" in data)) return false;

  const detail = (data as { detail: unknown }).detail;
  if (!Array.isArray(detail)) return false;

  return isErrorArray(detail);
}

export function isStringDetailResponse(data: unknown): data is { detail: string } {
  if (typeof data !== "object" || data === null) return false;
  if (!("detail" in data)) return false;

  return typeof (data as { detail: unknown }).detail === "string";
}

const STRING_DETAIL_MAP: Array<[RegExp, string, ((match: RegExpMatchArray) => Record<string, string>)?]> = [
  // Password-specific patterns BEFORE generic /invalid.*password/ to avoid false matches
  [/value length must be at most \d+/i, "validation:too-long"],
  [/value length must be at least \d+/i, "validation:too-short"],

  [/invalid or expired promo code/i, "validation:invalid-promo-code"],
  [/promo code is only available on the first deposit/i, "wallet:bonus-details.search.error-first-deposit-only"],
  [/promo code redemption limit reached/i, "wallet:bonus-details.search.error-limit-reached"],
  [/promo code has already been used/i, "wallet:bonus-details.search.error-already-used"],
  [/invalid.*verification.*code/i, "settings:errors.email-confirmation-failed"],
  [/invalid or expired verification token/i, "settings:errors.email-confirmation-failed"],
  [/^invalid 2fa code$/i, "validation:invalid-2fa-code"],
  [/invalid credentials/i, "validation:invalid-credentials"],
  [/invalid email or password/i, "validation:invalid-credentials"],
  [/^invalid password$/i, "validation:invalid-password"],
  [/invalid.*password/i, "validation:invalid-credentials"],
  [/current password is incorrect/i, "validation:current-password-incorrect"],
  [/incorrect.*password/i, "validation:invalid-credentials"],
  [/wrong.*password/i, "validation:invalid-credentials"],
  [/user not found/i, "validation:user-not-found"],
  [/account.*not.*found/i, "validation:user-not-found"],
  [/email.*already.*exists/i, "validation:email-already-exists"],
  [/email.*already.*registered/i, "validation:email-already-exists"],
  [/email is already in use/i, "validation:email-already-in-use"],
  [/unauthorized/i, "validation:unauthorized"],
  [/forbidden/i, "validation:forbidden"],
  [/not found/i, "validation:not-found"],
  [/^Withdrawal blocked until (.+)$/, "withdrawal:errors.withdrawal-blocked", (m) => ({ until: m[1] ?? "" })],
  [
    /^Maximum trade amount is ([\d.]+)$/,
    "trading:errors.create-bet.max-amount",
    (m) => ({ amount: parseFloat(m[1]!).toFixed(2) }),
  ],
  [
    /^Minimum trade amount is ([\d.]+)$/,
    "trading:errors.create-bet.min-amount",
    (m) => ({ amount: parseFloat(m[1]!).toFixed(2) }),
  ],
  [/^Not enough balance$/i, "trading:errors.create-bet.not-enough-balance"],
  [/^INSUFFICIENT_FUNDS$/i, "trading:errors.create-bet.not-enough-balance"],
  [/insufficient[\s_]funds/i, "trading:errors.create-bet.not-enough-balance"],
  [/^Not enough bonus balance$/i, "trading:errors.validation.insufficient-bonus-balance"],
  [/^NO_ACTIVE_BONUS$/i, "wallet:bonus-details.cancel-error"],
  [/^KYC_ESCALATION_PENDING$/i, "withdrawal:errors.kyc-escalation-pending"],
  [/^KYC_REQUIRED$/i, "withdrawal:kyc.description"],
  [/Market is closed at(?: bet)? settlement time/i, "trading:errors.create-bet.market-closed"],
];

export function isPayoutConditionsChangedError(error: unknown): boolean {
  const message = error instanceof ConnectError ? error.rawMessage : error instanceof Error ? error.message : null;
  return !!message && /^Payout conditions changed:/i.test(message);
}

export function mapStringDetail(detail: string): { key: string; params?: Record<string, string> } {
  for (const [pattern, key, paramExtractor] of STRING_DETAIL_MAP) {
    const match = detail.match(pattern);
    if (match) {
      return { key, params: paramExtractor ? paramExtractor(match) : undefined };
    }
  }
  return { key: detail };
}

export function extractPydanticErrors(data: unknown): PydanticError[] | null {
  if (isPydanticErrorArray(data)) {
    return data;
  }
  if (isPydanticDetailResponse(data)) {
    return data.detail;
  }
  return null;
}

/**
 * Parses "Validation failed: field X.Y.fieldName: Message; field ...: ..." format
 * returned by ConnectRPC server-side validation errors.
 * Returns individual sub-messages.
 */
function parseValidationFailed(message: string): string[] | null {
  if (!/^Validation failed:/i.test(message)) return null;

  const withoutPrefix = message.replace(/^Validation failed:\s*/i, "");
  const parts = withoutPrefix.split(/;\s*field\s+/);

  return parts
    .map((part) => {
      // each part: "field X.Y.z: Message" or (after split) "X.Y.z: Message"
      const colonIdx = part.indexOf(":");
      if (colonIdx === -1) return part.trim();
      return part.slice(colonIdx + 1).trim();
    })
    .filter(Boolean);
}

/**
 * Extracts a mapped error key from a ConnectRPC error or any unknown error.
 * Returns a translation key + optional params ready to pass to t().
 */
export function mapRpcError(error: unknown): { key: string; params?: Record<string, string> } {
  let message: string | null = null;
  if (error instanceof ConnectError) {
    message = error.rawMessage;
  } else if (error instanceof Error) {
    message = error.message;
  }
  if (!message) return { key: "error:unknown-error" };

  if (message.trimStart().startsWith("{")) {
    try {
      const parsed = JSON.parse(message);
      if (parsed && typeof parsed.message === "string") message = parsed.message;
    } catch {}
  }

  return mapStringDetail(message!);
}

/**
 * Like mapRpcError but returns ALL mapped errors (useful for "Validation failed:" with multiple fields).
 * Falls back to single-element array for non-validation errors.
 */
export function mapRpcErrors(error: unknown): Array<{ key: string; params?: Record<string, string> }> {
  // Use rawMessage (without "[code] " prefix) for correct pattern matching
  let message = error instanceof ConnectError ? error.rawMessage : error instanceof Error ? error.message : null;

  if (!message) return [{ key: "error:unknown-error" }];

  // If the message is a JSON object (e.g. {"code":"invalid_argument","message":"..."}) — extract the message field
  if (message.trimStart().startsWith("{")) {
    try {
      const parsed = JSON.parse(message);
      if (parsed && typeof parsed.message === "string") {
        message = parsed.message;
      }
    } catch {}
  }

  const subMessages = parseValidationFailed(message!);
  if (subMessages && subMessages.length > 0) {
    return subMessages.map(mapStringDetail);
  }

  return [mapStringDetail(message!)];
}
